import React, { useState, useMemo, useRef, useEffect } from 'react'
import styled from 'styled-components'
import Input from 'components/atoms/Input'
import Checkbox from 'components/atoms/Checkbox'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Chip from '@mui/material/Chip'
import useDebounce from 'utils/hooks/useDebounce'
import { getAssignedUsers } from 'utils/bpData'
import { useSelector } from 'react-redux'
import { getCurrentUser } from 'features/auth/ducks/selectors'

const ViewableBySelect = ({
  selectedUsers = [],
  setSelectedUsers,
  placeholder = 'Filter by files viewable by...',
  bp
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [showSelected, setShowSelected] = useState(false)
  const resultsRef = useRef(null)
  const inputRef = useRef(null)
  const debouncedValue = useDebounce(searchQuery, 300)
  const currentUser = useSelector(getCurrentUser)

  // Get all assigned users from the BP
  const userOptions = useMemo(() => {
    if (!bp) return []

    const assignedUsers = getAssignedUsers(bp) || []

    // Deduplicate by user ID
    const uniqueUsers = new Map()
    assignedUsers.forEach(user => {
      if (!uniqueUsers.has(user.id)) {
        uniqueUsers.set(user.id, {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`.trim() || user.email,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          isCurrentUser: user.id === currentUser?.id
        })
      }
    })

    // Sort: current user first, then alphabetically
    return Array.from(uniqueUsers.values()).sort((a, b) => {
      if (a.isCurrentUser) return -1
      if (b.isCurrentUser) return 1
      return a.name.localeCompare(b.name)
    })
  }, [bp, currentUser])

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!debouncedValue) return userOptions

    const query = debouncedValue.toLowerCase().trim()
    return userOptions.filter(user => {
      const searchableText = [
        user.name,
        user.email,
        user.firstName,
        user.lastName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(query)
    })
  }, [userOptions, debouncedValue])

  // Get selected user names for display
  const selectedUserNames = useMemo(() => {
    if (!selectedUsers.length || !userOptions.length) return []
    return selectedUsers
      .map(id => userOptions.find(user => user.id === id)?.name)
      .filter(Boolean)
  }, [selectedUsers, userOptions])

  const handleUserToggle = userId => {
    const newSelected = selectedUsers.includes(userId)
      ? selectedUsers.filter(id => id !== userId)
      : [...selectedUsers, userId]
    setSelectedUsers(newSelected)
  }

  const handleClearAll = () => {
    setSelectedUsers([])
  }

  const handleRemoveUser = userId => {
    setSelectedUsers(selectedUsers.filter(id => id !== userId))
  }

  const handleSearch = (_, query) => {
    setSearchQuery(query)
  }

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        const input = inputRef.current.querySelector('input')
        if (input) {
          input.focus()
        }
      }, 100)
    }
  }, [open])

  const displayText =
    selectedUsers.length > 0
      ? `${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''} selected`
      : placeholder

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Container>
        <InputWrapper onClick={() => setOpen(true)}>
          <DisplayInput hasValue={selectedUsers.length > 0}>
            <DisplayText hasValue={selectedUsers.length > 0}>
              {displayText}
            </DisplayText>
            {selectedUsers.length > 0 && (
              <ActionButtons>
                <ActionButton
                  onClick={e => {
                    e.stopPropagation()
                    setShowSelected(!showSelected)
                  }}
                >
                  {showSelected ? 'Hide' : 'Show'} Selected
                </ActionButton>
                <ActionButton
                  onClick={e => {
                    e.stopPropagation()
                    handleClearAll()
                  }}
                >
                  Clear All
                </ActionButton>
              </ActionButtons>
            )}
          </DisplayInput>
          <DropdownIcon>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
              <path d='M7 10l5 5 5-5z' />
            </svg>
          </DropdownIcon>
        </InputWrapper>

        {/* Selected users display */}
        {selectedUsers.length > 0 && showSelected && (
          <SelectedWrapper>
            {selectedUserNames.map((userName, index) => (
              <StyledChip
                key={selectedUsers[index]}
                label={userName}
                onDelete={() => handleRemoveUser(selectedUsers[index])}
                size='small'
              />
            ))}
          </SelectedWrapper>
        )}

        {open && (
          <Dropdown ref={resultsRef}>
            <SearchWrapper>
              <Input
                ref={inputRef}
                onChange={handleSearch}
                value={searchQuery}
                name='searchQuery'
                placeholder='Search users...'
                nomarginbottom
              />
            </SearchWrapper>

            <ResultsWrapper>
              {filteredUsers.length === 0 ? (
                <NoResults>No users found</NoResults>
              ) : (
                filteredUsers.map(user => (
                  <UserItem key={user.id} isCurrentUser={user.isCurrentUser}>
                    <Checkbox
                      label={
                        user.isCurrentUser ? `${user.name} (You)` : user.name
                      }
                      name={user.id}
                      value={selectedUsers.includes(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                      noMargin
                    />
                  </UserItem>
                ))
              )}
            </ResultsWrapper>

            {selectedUsers.length > 0 && (
              <DropdownFooter>
                <FooterText>{selectedUsers.length} selected</FooterText>
                <FooterButton onClick={handleClearAll}>Clear All</FooterButton>
              </DropdownFooter>
            )}
          </Dropdown>
        )}
      </Container>
    </ClickAwayListener>
  )
}

export default ViewableBySelect

const Container = styled.div`
  position: relative;
  width: 100%;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const InputWrapper = styled.div`
  position: relative;
  cursor: pointer;
`

const DisplayInput = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: 6px;
  padding: 10px 35px 10px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  min-height: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`

const DisplayText = styled.span`
  color: ${({ theme, hasValue }) =>
    hasValue ? theme.colors.grey[700] : theme.colors.grey[500]};
  font-weight: 400;
`

const DropdownIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.54);
  pointer-events: none;
  display: flex;
  align-items: center;

  svg {
    width: 24px;
    height: 24px;
  }
`

const SelectedWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 100px;
  overflow-y: auto;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.primary}05;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.primary}10;
`

const StyledChip = styled(Chip)`
  && {
    height: 24px;
    font-size: 11px;

    .MuiChip-deleteIcon {
      font-size: 14px;
    }
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  margin-top: 4px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const SearchWrapper = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
`

const ResultsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 300px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey[100]};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grey[400]};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.colors.grey[500]};
    }
  }
`

const UserItem = styled.div`
  padding: 10px 16px;
  transition: all 0.2s ease;
  background-color: ${({ isCurrentUser, theme }) =>
    isCurrentUser ? theme.colors.primary + '08' : 'transparent'};
  border-left: ${({ isCurrentUser, theme }) =>
    isCurrentUser
      ? `3px solid ${theme.colors.primary}`
      : '3px solid transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[50]};
    padding-left: 20px;
  }
`

const UserEmail = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-top: 4px;
  margin-left: 24px;
  line-height: 1.3;
`

const NoResults = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 14px;
`

const DropdownFooter = styled.div`
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FooterText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const FooterButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`
